import React, { useState, useEffect } from 'react';
import BiologicalLayout from '@/components/layout/BiologicalLayout';
import { useAuth, API } from '@/App';
import { ChevronDown, ChevronRight } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════════════
// BIOLOGICAL INTERFACE — YOUR DESIGNED PATH
// ══════════════════════════════════════════════════════════════════════════════

// Day phases — the rhythm of the designed path
const DAY_PHASES = [
  {
    key: 'wake',
    blocks: ['wake'],
    label: 'Wake',
    purpose: 'This phase supports early stability and smooth transition into the day.'
  },
  {
    key: 'morning',
    blocks: ['morning', 'mid_morning'],
    label: 'Morning',
    purpose: 'This phase builds the foundation for the day — timing, rhythm, and core support.'
  },
  {
    key: 'midday',
    blocks: ['lunch'],
    label: 'Midday',
    purpose: 'This phase bridges morning energy into the afternoon and protects timing balance.'
  },
  {
    key: 'afternoon',
    blocks: ['afternoon'],
    label: 'Afternoon',
    purpose: 'This phase protects post-lunch balance and sustains energy through the lower part of the day.'
  },
  {
    key: 'evening',
    blocks: ['evening', 'dinner'],
    label: 'Evening',
    purpose: 'This phase helps reduce evening variability and prepares the system for rest.'
  },
  {
    key: 'night',
    blocks: ['night', 'sleep'],
    label: 'Night',
    purpose: 'This phase supports deep recovery, neurological repair, and readiness for the next day.'
  }
];

// Subtle type indicators
const TYPE_LABELS = {
  regenflo: 'support',
  medication: 'medication',
  food: 'nourishment',
  lifeflo: 'rhythm'
};

// Convert 24h to readable time
function formatTime(t24) {
  if (!t24) return '';
  const [h, m] = t24.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Watchpoint detection
function getWatchpoint(item) {
  const instruction = (item.instruction || '').toLowerCase();
  const name = (item.name || '').toLowerCase();

  if (instruction.includes('empty stomach')) return 'best on empty stomach';
  if (instruction.includes('before breakfast')) return 'before eating';
  if (instruction.includes('before lunch')) return 'before eating';
  if (instruction.includes('after waking')) return 'soon after waking';
  if (instruction.includes('before sleep') || instruction.includes('before bed')) return 'before rest';
  if (instruction.includes('food gap') || instruction.includes('30 min')) return 'timing matters';
  if (instruction.includes('disturbs sleep')) return 'watch sleep impact';
  return null;
}


export default function SubscriberProtocol() {
  const { user } = useAuth();
  const [proto, setProto] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({});

  const subscriberId = user?.role === 'caregiver' ? user?.linked_subscriber_id : user?.id;

  useEffect(() => {
    if (!subscriberId) return;
    const token = localStorage.getItem('rf_access_token');
    if (!token) { setLoading(false); return; }

    const headers = { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' };

    fetch(`${API}/executable-protocol/${subscriberId}`, { headers })
      .then(res => res.ok ? res.json() : null)
      .then(protoData => {
        setProto(protoData);
        if (protoData?.has_protocol) {
          const pid = protoData.protocol.id;
          return fetch(`${API}/v3/phases/${pid}/schedule-items`, { headers })
            .then(res => res.ok ? res.json() : null);
        }
        return null;
      })
      .then(itemsData => {
        if (itemsData) {
          setData(itemsData);
          // Expand all phases by default
          const expanded = {};
          DAY_PHASES.forEach(p => { expanded[p.key] = true; });
          setExpandedPhases(expanded);
        }
      })
      .finally(() => setLoading(false));
  }, [subscriberId]);

  const togglePhase = (key) => {
    setExpandedPhases(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <BiologicalLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-bio-accent/30 border-t-bio-accent rounded-full animate-spin" />
        </div>
      </BiologicalLayout>
    );
  }

  if (!proto?.has_protocol) {
    return (
      <BiologicalLayout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <p className="text-xl font-['Outfit'] font-light text-bio-primary mb-4">
            Your designed path is being prepared
          </p>
          <p className="text-sm text-bio-muted leading-relaxed max-w-md mx-auto">
            Once your biological foundation is established, a personalized daily structure will be designed to support your stability.
          </p>
        </div>
      </BiologicalLayout>
    );
  }

  const protocol = proto.protocol;
  const grouped = data?.grouped_by_block || {};

  // Compute day elapsed
  const startDate = protocol?.start_date ? new Date(protocol.start_date) : null;
  const daysElapsed = startDate ? Math.max(1, Math.floor((new Date() - startDate) / 86400000) + 1) : null;

  // Build opening narrative
  const phaseDescription = protocol.description || protocol.phase_objectives?.join('. ') || '';
  const openingLine = phaseDescription ||
    'This phase is designed to reduce variability, support smoother timing, and protect your daily neurological stability.';

  return (
    <BiologicalLayout>
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-10 sm:py-14" data-testid="protocol-designed-path">

        {/* ─── Title ─────────────────────────────────────── */}
        <div className="mb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-bio-faint mb-3 font-['Work_Sans']"
             data-testid="protocol-label">
            Your Designed Path
          </p>
          <h1 className="text-3xl sm:text-4xl font-['Outfit'] font-light text-bio-primary leading-tight"
              data-testid="protocol-title">
            {protocol.phase_name || 'Your Protocol'}
          </h1>
        </div>


        {/* ─── Opening Paragraph ─────────────────────────── */}
        <section className="mb-14" data-testid="protocol-opening">
          <p className="text-base sm:text-lg text-bio-body leading-[1.8] font-['Work_Sans'] font-light">
            {openingLine}
          </p>
        </section>


        {/* ─── Phase Summary ────────────────────────────── */}
        <section className="mb-14" data-testid="protocol-phase-summary">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {protocol.phase_number && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-bio-faint font-['Work_Sans']">Phase</p>
                <p className="text-bio-body text-sm font-['Work_Sans'] mt-0.5">{protocol.phase_number}</p>
              </div>
            )}
            {daysElapsed && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-bio-faint font-['Work_Sans']">Day</p>
                <p className="text-bio-body text-sm font-['Work_Sans'] mt-0.5">{daysElapsed}</p>
              </div>
            )}
            {data?.total && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-bio-faint font-['Work_Sans']">Daily Structure</p>
                <p className="text-bio-body text-sm font-['Work_Sans'] mt-0.5">{data.total} elements</p>
              </div>
            )}
            {protocol.status && (
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-bio-faint font-['Work_Sans']">Status</p>
                <p className="text-bio-body text-sm font-['Work_Sans'] mt-0.5 capitalize">{protocol.status}</p>
              </div>
            )}
          </div>
        </section>


        {/* ─── The Designed Day ──────────────────────────── */}
        <div className="space-y-10">
          {DAY_PHASES.map(phase => {
            // Collect items for this phase
            const phaseItems = [];
            phase.blocks.forEach(block => {
              const blockItems = grouped[block] || [];
              blockItems.forEach(item => phaseItems.push({ ...item, _block: block }));
            });

            if (phaseItems.length === 0) return null;

            const isExpanded = expandedPhases[phase.key] !== false;

            return (
              <section key={phase.key} data-testid={`protocol-phase-${phase.key}`}>
                {/* Phase Header */}
                <button
                  onClick={() => togglePhase(phase.key)}
                  className="w-full flex items-start gap-3 text-left group mb-1"
                  data-testid={`protocol-phase-toggle-${phase.key}`}
                >
                  <div className="flex items-center gap-2 flex-shrink-0 pt-0.5">
                    {isExpanded
                      ? <ChevronDown className="w-3.5 h-3.5 text-bio-ghost" strokeWidth={1} />
                      : <ChevronRight className="w-3.5 h-3.5 text-bio-ghost" strokeWidth={1} />
                    }
                    <p className="text-[11px] uppercase tracking-[0.25em] text-bio-subtle font-['Work_Sans'] group-hover:text-bio-muted transition-colors">
                      {phase.label}
                    </p>
                  </div>
                  <span className="text-[10px] text-bio-ghost font-['Work_Sans'] mt-0.5">{phaseItems.length}</span>
                </button>

                {/* Purpose line */}
                {isExpanded && (
                  <p className="text-[11px] text-bio-faint leading-relaxed font-['Work_Sans'] ml-[22px] mb-5"
                     data-testid={`protocol-purpose-${phase.key}`}>
                    {phase.purpose}
                  </p>
                )}

                {/* Items */}
                {isExpanded && (
                  <div className="ml-[22px] space-y-0">
                    {phaseItems.map((item, i) => {
                      const typeLabel = TYPE_LABELS[item.event_type] || item.event_type;
                      const watchpoint = getWatchpoint(item);

                      return (
                        <div
                          key={`${item.code || i}-${item.planned_time}`}
                          className="flex items-start gap-4 py-2.5"
                          style={{ borderBottom: i < phaseItems.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}
                          data-testid={`protocol-item-${item.code || i}`}
                        >
                          {/* Time */}
                          <span className="text-[11px] text-bio-faint font-['Work_Sans'] w-[60px] flex-shrink-0 pt-0.5 tabular-nums">
                            {formatTime(item.planned_time)}
                          </span>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2">
                              <span className="text-sm text-bio-strong font-['Work_Sans']"
                                    data-testid={`protocol-item-name-${item.code || i}`}>
                                {item.name}
                              </span>
                              <span className="text-[10px] text-bio-ghost font-['Work_Sans']">{typeLabel}</span>
                            </div>

                            {/* Detail/dose — only when meaningful */}
                            {item.planned_detail_dose && item.planned_detail_dose !== 'As directed' && (
                              <p className="text-[11px] text-bio-faint font-['Work_Sans'] mt-0.5">
                                {item.planned_detail_dose}
                              </p>
                            )}

                            {/* Instruction — only when meaningful */}
                            {item.instruction && (
                              <p className="text-[11px] text-bio-faint font-['Work_Sans'] mt-0.5">
                                {item.instruction}
                              </p>
                            )}

                            {/* Watchpoint — subtle */}
                            {watchpoint && (
                              <span className="inline-block text-[10px] text-bio-accent-soft font-['Work_Sans'] mt-1"
                                    data-testid={`protocol-watchpoint-${item.code || i}`}>
                                {watchpoint}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Phase divider */}
                <div className="mt-6" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }} />
              </section>
            );
          })}
        </div>


        {/* ─── Closing Line ──────────────────────────────── */}
        <section className="mt-16 mb-8" data-testid="protocol-closing">
          <p className="text-[11px] text-bio-faint leading-relaxed font-['Work_Sans'] text-center max-w-lg mx-auto">
            Consistency in this structure helps your system remain smoother and more predictable.
          </p>
        </section>

        {/* ─── Closing breath ──────────────────────────── */}
        <div className="pt-4 pb-4">
          <div className="w-8 h-px bg-bio-border mx-auto" />
        </div>

      </div>
    </BiologicalLayout>
  );
}
